/**
 * Production-safe logging utility
 * Automatically handles different log levels based on environment
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

class Logger {
  constructor() {
    this.level = this.getLogLevel();
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  getLogLevel() {
    const envLevel = process.env.REACT_APP_LOG_LEVEL || 'INFO';
    return LOG_LEVELS[envLevel.toUpperCase()] ?? LOG_LEVELS.INFO;
  }

  shouldLog(level) {
    return level <= this.level;
  }

  formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString();
    const levelName = Object.keys(LOG_LEVELS)[level];

    if (this.isDevelopment) {
      return { timestamp, level: levelName, message, context };
    }

    // In production, sanitize and structure logs
    return {
      timestamp,
      level: levelName,
      message: typeof message === 'string' ? message : 'System event',
      ...(Object.keys(context).length > 0 && { context }),
    };
  }

  log(level, message, context = {}) {
    if (!this.shouldLog(level)) return;

    const logData = this.formatMessage(level, message, context);

    // In development, use console methods for better debugging
    if (this.isDevelopment) {
      switch (level) {
        case LOG_LEVELS.ERROR:
          console.error(logData);
          break;
        case LOG_LEVELS.WARN:
          console.warn(logData);
          break;
        case LOG_LEVELS.DEBUG:
          console.debug(logData);
          break;
        default:
          console.log(logData);
      }
      return;
    }

    // In production, could send to logging service
    // For now, only log errors to console
    if (level === LOG_LEVELS.ERROR) {
      console.error(logData);
    }
  }

  error(message, context) {
    this.log(LOG_LEVELS.ERROR, message, context);
  }

  warn(message, context) {
    this.log(LOG_LEVELS.WARN, message, context);
  }

  info(message, context) {
    this.log(LOG_LEVELS.INFO, message, context);
  }

  debug(message, context) {
    this.log(LOG_LEVELS.DEBUG, message, context);
  }

  // Utility methods for common use cases
  apiCall(endpoint, method = 'GET', context = {}) {
    this.debug(`API Call: ${method} ${endpoint}`, context);
  }

  apiResponse(endpoint, status, context = {}) {
    if (status >= 400) {
      this.error(`API Error: ${endpoint} returned ${status}`, context);
    } else {
      this.debug(`API Success: ${endpoint} returned ${status}`, context);
    }
  }

  userAction(action, context = {}) {
    this.info(`User Action: ${action}`, context);
  }

  authEvent(event, userId = null, context = {}) {
    this.info(`Auth Event: ${event}`, { userId, ...context });
  }

  performanceMetric(metric, value, context = {}) {
    this.info(`Performance: ${metric}`, { value, ...context });
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;

// Named exports for convenience
export const {
  error,
  warn,
  info,
  debug,
  apiCall,
  apiResponse,
  userAction,
  authEvent,
  performanceMetric,
} = logger;
